import { useTranslate } from "ra-core";
import { useEffect, useRef, useState } from "react";

import type { DatosPanel } from "../dominio/panelInicio";

type Dia = DatosPanel["serie"][number];

const ALTO = 220;
const M = { arriba: 12, derecha: 8, abajo: 26, izquierda: 34 };

/** Escala «bonita» del eje: 0, la mitad y el máximo redondeado. */
const marcas = (max: number) => {
  if (max <= 4) return [0, 2, 4];
  const paso = Math.pow(10, Math.floor(Math.log10(max)));
  const tope = Math.ceil(max / paso) * paso;
  return [0, tope / 2, tope];
};

const fechaCorta = (dia: string, total: number) => {
  const [a, m, d] = dia.split("-").map(Number);
  const f = new Date(a, m - 1, d);
  return total <= 7
    ? f.toLocaleDateString("es-ES", { weekday: "short", day: "numeric" })
    : `${d}/${m}`;
};

/** Barra con la punta redondeada (4 px) y la base recta. */
const barra = (x: number, y: number, w: number, h: number, r: number) => {
  if (h <= 0) return "";
  const rr = Math.min(r, w / 2, h);
  return `M${x},${y + h} V${y + rr} Q${x},${y} ${x + rr},${y} H${x + w - rr} Q${x + w},${y} ${x + w},${y + rr} V${y + h} Z`;
};

/**
 * Actividad por día del panel de Inicio (45): llamadas (contestadas en azul pleno, sin respuesta
 * en azul claro encima) y, como punto, las consultorías agendadas ese día. Un solo eje (todo son
 * recuentos). Al pasar el ratón, el detalle del día; debajo, la misma información en tabla.
 */
export const GraficoActividad = ({ serie }: { serie: Dia[] }) => {
  const translate = useTranslate();
  const caja = useRef<HTMLDivElement>(null);
  const [ancho, setAncho] = useState(640);
  const [activo, setActivo] = useState<number | null>(null);

  useEffect(() => {
    const el = caja.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setAncho(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const n = Math.max(serie.length, 1);
  const max = Math.max(1, ...serie.map((d) => Math.max(d.llamadas, d.citas)));
  const ticks = marcas(max);
  const tope = ticks[ticks.length - 1];
  const plotW = Math.max(ancho - M.izquierda - M.derecha, 10);
  const plotH = ALTO - M.arriba - M.abajo;
  const banda = plotW / n;
  const anchoBarra = Math.max(2, Math.min(24, banda * 0.6));
  const y = (v: number) => M.arriba + plotH - (v / tope) * plotH;
  const cadaCuanto = Math.ceil(n / Math.max(1, Math.floor(plotW / 56)));
  const dia = activo != null ? serie[activo] : null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm bg-[var(--serie-llamadas)]" />
          {translate("nevora.inicio.grafico.contestadas")}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm bg-[var(--serie-llamadas)] opacity-35" />
          {translate("nevora.inicio.grafico.sin_respuesta")}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2.5 rounded-full bg-[var(--serie-citas)]" />
          {translate("nevora.inicio.grafico.citas")}
        </span>
      </div>
      <div ref={caja} className="relative w-full">
        <svg
          width={ancho}
          height={ALTO}
          role="img"
          aria-label={translate("nevora.inicio.grafico.titulo")}
          onMouseLeave={() => setActivo(null)}
          className="block"
        >
          {ticks.map((t) => (
            <g key={t}>
              <line
                x1={M.izquierda}
                x2={ancho - M.derecha}
                y1={y(t)}
                y2={y(t)}
                stroke="var(--border)"
                strokeWidth={1}
              />
              <text
                x={M.izquierda - 8}
                y={y(t)}
                dy="0.32em"
                textAnchor="end"
                className="fill-muted-foreground text-[11px] tabular-nums"
              >
                {t}
              </text>
            </g>
          ))}
          {serie.map((d, i) => {
            const cx = M.izquierda + banda * i + banda / 2;
            const x = cx - anchoBarra / 2;
            const hC = (d.contestadas / tope) * plotH;
            const hS = ((d.llamadas - d.contestadas) / tope) * plotH;
            const base = M.arriba + plotH;
            const hueco = hC > 0 && hS > 0 ? 2 : 0;
            return (
              <g key={d.dia}>
                {activo === i ? (
                  <rect
                    x={M.izquierda + banda * i}
                    y={M.arriba}
                    width={banda}
                    height={plotH}
                    className="fill-foreground/[0.04]"
                  />
                ) : null}
                <path
                  d={
                    hS > 0
                      ? `M${x},${base} V${base - hC} H${x + anchoBarra} V${base} Z`
                      : barra(x, base - hC, anchoBarra, hC, 4)
                  }
                  fill="var(--serie-llamadas)"
                />
                {hS > 0 ? (
                  <path
                    d={barra(x, base - hC - hueco - hS, anchoBarra, hS, 4)}
                    fill="var(--serie-llamadas)"
                    opacity={0.35}
                  />
                ) : null}
                {d.citas > 0 ? (
                  <circle
                    cx={cx}
                    cy={y(d.citas)}
                    r={4.5}
                    fill="var(--serie-citas)"
                    stroke="var(--card)"
                    strokeWidth={2}
                  />
                ) : null}
                {i % cadaCuanto === 0 ? (
                  <text
                    x={cx}
                    y={ALTO - 8}
                    textAnchor="middle"
                    className="fill-muted-foreground text-[11px]"
                  >
                    {fechaCorta(d.dia, n)}
                  </text>
                ) : null}
                <rect
                  x={M.izquierda + banda * i}
                  y={M.arriba}
                  width={banda}
                  height={plotH + M.abajo}
                  fill="transparent"
                  onMouseEnter={() => setActivo(i)}
                />
              </g>
            );
          })}
        </svg>
        {dia ? (
          <div
            className="pointer-events-none absolute top-1 z-10 min-w-40 rounded-lg border border-border/60 bg-popover px-3 py-2 text-xs shadow-elevada"
            style={{
              left: Math.min(
                Math.max(
                  M.izquierda + banda * (activo ?? 0) + banda / 2 - 80,
                  0,
                ),
                Math.max(ancho - 170, 0),
              ),
            }}
            role="status"
          >
            <p className="font-medium text-foreground">
              {new Date(`${dia.dia}T12:00:00`).toLocaleDateString("es-ES", {
                weekday: "long",
                day: "numeric",
                month: "long",
              })}
            </p>
            <dl className="mt-1 grid grid-cols-[1fr_auto] gap-x-4 gap-y-0.5 tabular-nums">
              <dt className="text-muted-foreground">
                {translate("nevora.inicio.grafico.llamadas")}
              </dt>
              <dd className="text-right text-foreground">{dia.llamadas}</dd>
              <dt className="text-muted-foreground">
                {translate("nevora.inicio.grafico.contestadas")}
              </dt>
              <dd className="text-right text-foreground">{dia.contestadas}</dd>
              <dt className="text-muted-foreground">
                {translate("nevora.inicio.grafico.citas")}
              </dt>
              <dd className="text-right text-foreground">{dia.citas}</dd>
            </dl>
          </div>
        ) : null}
      </div>
      <details className="text-xs text-muted-foreground">
        <summary className="cursor-pointer select-none hover:text-foreground">
          {translate("nevora.inicio.grafico.ver_tabla")}
        </summary>
        <table className="mt-2 w-full text-left">
          <thead>
            <tr>
              <th className="py-1 font-medium">
                {translate("nevora.inicio.grafico.dia")}
              </th>
              <th className="py-1 text-right font-medium">
                {translate("nevora.inicio.grafico.llamadas")}
              </th>
              <th className="py-1 text-right font-medium">
                {translate("nevora.inicio.grafico.contestadas")}
              </th>
              <th className="py-1 text-right font-medium">
                {translate("nevora.inicio.grafico.citas")}
              </th>
            </tr>
          </thead>
          <tbody>
            {serie.map((d) => (
              <tr key={d.dia} className="border-t border-border/50">
                <td className="py-1">{d.dia.split("-").reverse().join("/")}</td>
                <td className="py-1 text-right text-foreground">
                  {d.llamadas}
                </td>
                <td className="py-1 text-right text-foreground">
                  {d.contestadas}
                </td>
                <td className="py-1 text-right text-foreground">{d.citas}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </div>
  );
};

/** Minilínea de 2 px para las cifras clave (los últimos días del periodo). */
export const Minilinea = ({ valores }: { valores: number[] }) => {
  // Sin nada que dibujar (todo a cero), mejor nada que una raya plana.
  if (valores.length < 2 || !valores.some((x) => x > 0)) return null;
  const w = 88;
  const h = 26;
  const max = Math.max(1, ...valores);
  const puntos = valores
    .map(
      (v, i) =>
        `${((i / (valores.length - 1)) * (w - 4) + 2).toFixed(1)},${(h - 3 - (v / max) * (h - 6)).toFixed(1)}`,
    )
    .join(" ");
  return (
    <svg width={w} height={h} aria-hidden className="shrink-0">
      <polyline
        points={puntos}
        fill="none"
        stroke="var(--serie-llamadas)"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
